export const PUBLIC_APP_FAMILIES = Object.freeze([
  { name: 'Amazon Seller', canonicalSlug: 'amazonSeller', publicSlug: 'amazon-seller', memberSlugs: ['amazonSeller', 'amazonUS'], routeSlugs: ['amazon-seller', 'amazon', 'amazon-private-app', 'amazon-us'] },
  { name: 'Amazon Vendor', canonicalSlug: 'amazonVendor', publicSlug: 'amazon-vendor', memberSlugs: ['amazonVendor', 'amazonVendorShipping'], routeSlugs: ['amazon-vendor', 'amazon-vendor-shipping'] },
  { name: 'Asana', canonicalSlug: 'asanaNew', publicSlug: 'asana', memberSlugs: ['asanaNew', 'asana'], routeSlugs: ['asana', 'asana-new', 'asana-asana-new'] },
  { name: 'BambooHR', canonicalSlug: 'bamboohr', publicSlug: 'bamboohr', memberSlugs: ['bamboohr', 'bambooHrV2'], routeSlugs: ['bamboohr', 'bamboo-hr-v2', 'bamboohr-legacy'] },
  { name: 'DateX', canonicalSlug: 'dateX', publicSlug: 'datex', memberSlugs: ['dateX', 'dateXNew'], routeSlugs: ['datex', 'date-x', 'date-x-new', 'datex-legacy'] },
  { name: 'Follow Up Boss', canonicalSlug: 'followUpBoss', publicSlug: 'follow-up-boss', memberSlugs: ['followUpBoss', 'followUpBossV2'], routeSlugs: ['follow-up-boss', 'follow-up-boss-v2', 'follow-up-boss-legacy'] },
  { name: 'HubSpot', canonicalSlug: 'hubspot', publicSlug: 'hubspot', memberSlugs: ['hubspot', 'hubspotApp'], routeSlugs: ['hubspot', 'hubspot-app', 'hubspot-legacy'] },
  { name: 'Instantly.ai', canonicalSlug: 'instantlyai', publicSlug: 'instantly-ai', memberSlugs: ['instantlyai', 'instantly'], routeSlugs: ['instantly-ai', 'instantly', 'instantlyai'] },
  { name: 'Microsoft 365', canonicalSlug: 'azurePortal', publicSlug: 'microsoft-365', memberSlugs: ['azurePortal', 'microsoft365'], routeSlugs: ['microsoft-365', 'azure-portal', 'microsoft365', 'microsoft-365-legacy'] },
  { name: 'NetSuite', canonicalSlug: 'netsuite', publicSlug: 'netsuite-advanced', memberSlugs: ['netsuite', 'netSuiteBasic', 'netSuiteRESTAPI'], routeSlugs: ['netsuite-advanced', 'netsuite', 'net-suite-basic', 'net-suite-rest-api', 'netsuite-basic', 'netsuite-rest-api'] },
  { name: 'QuickBooks Online', canonicalSlug: 'quickbooksOnlineAccounting', publicSlug: 'quickbooks-online', memberSlugs: ['quickbooksOnlineAccounting', 'quickBooksOnline'], routeSlugs: ['quickbooks-online', 'quickbooks-online-accounting', 'quick-books-online'] },
  { name: 'Smartsheet', canonicalSlug: 'smartsheetLegacy', publicSlug: 'smartsheet', memberSlugs: ['smartsheetLegacy', 'smartSheet'], routeSlugs: ['smartsheet', 'smart-sheet', 'smartsheet-legacy'] },
  { name: 'SOS Inventory', canonicalSlug: 'sOSInventoryLegacy', publicSlug: 'sos-inventory', memberSlugs: ['sOSInventoryLegacy', 'sOSInventory'], routeSlugs: ['sos-inventory', 's-os-inventory', 's-os-inventory-legacy', 'sos-inventory-legacy'] },
  { name: 'WooCommerce', canonicalSlug: 'woocommerceLegacy', publicSlug: 'woocommerce', memberSlugs: ['woocommerceLegacy', 'wooCommerce'], routeSlugs: ['woocommerce', 'woo-commerce', 'woocommerce-legacy'] }
]);

const FAMILY_BY_MEMBER_SLUG = new Map();
const FAMILY_BY_ROUTE_SLUG = new Map();

for (const family of PUBLIC_APP_FAMILIES) {
  for (const memberSlug of family.memberSlugs) {
    FAMILY_BY_MEMBER_SLUG.set(memberSlug, family);
    FAMILY_BY_ROUTE_SLUG.set(memberSlug, family);
  }

  for (const routeSlug of family.routeSlugs) {
    FAMILY_BY_ROUTE_SLUG.set(routeSlug, family);
  }
}

export const getPublicAppFamilyByMindcloudSlug = (slug) => {
  return FAMILY_BY_MEMBER_SLUG.get(String(slug || '').trim()) || null;
};

export const getPublicAppFamilyByRouteSlug = (slug) => {
  return FAMILY_BY_ROUTE_SLUG.get(String(slug || '').trim()) || null;
};

export const getCanonicalPublicAppMindcloudSlug = (slug) => {
  const cleanSlug = String(slug || '').trim();

  return getPublicAppFamilyByMindcloudSlug(cleanSlug)?.canonicalSlug || cleanSlug;
};

export const getCanonicalPublicAppSlug = (slug) => {
  return getPublicAppFamilyByMindcloudSlug(slug)?.publicSlug || '';
};

export const getPublicAppFamilyRouteSlugs = (slug) => {
  const family = getPublicAppFamilyByMindcloudSlug(slug);

  return family ? [...new Set([...family.routeSlugs, ...family.memberSlugs])] : [];
};

export const isCanonicalPublicAppMindcloudSlug = (slug) => {
  const family = getPublicAppFamilyByMindcloudSlug(slug);

  return !family || family.canonicalSlug === slug;
};

export const filterCanonicalPublicApps = (apps = []) => {
  const appList = Array.isArray(apps) ? apps : [];
  const getAppSlug = (app) => String(app?.slug || app?.id || '').trim();
  const appSlugs = new Set(appList.map(getAppSlug).filter(Boolean));

  for (const family of PUBLIC_APP_FAMILIES) {
    const hasFamilyMember = family.memberSlugs.some((slug) => appSlugs.has(slug));

    if (hasFamilyMember && !appSlugs.has(family.canonicalSlug)) {
      throw new Error(`Canonical public app ${family.canonicalSlug} is missing for ${family.name}.`);
    }
  }

  return appList.filter((app) => isCanonicalPublicAppMindcloudSlug(getAppSlug(app)));
};

const getLatestTimestamp = (left, right) => {
  const leftTimestamp = new Date(left || 0).getTime();
  const rightTimestamp = new Date(right || 0).getTime();

  if (!Number.isFinite(leftTimestamp)) {
    return right || null;
  }

  if (!Number.isFinite(rightTimestamp)) {
    return left || null;
  }

  return rightTimestamp > leftTimestamp ? right : left;
};

export const canonicalizePublicAppPairs = (pairs = []) => {
  const pairByKey = new Map();

  for (const pair of Array.isArray(pairs) ? pairs : []) {
    const sourceMindcloudSlug = getCanonicalPublicAppMindcloudSlug(pair?.sourceMindcloudSlug);
    const targetMindcloudSlug = getCanonicalPublicAppMindcloudSlug(pair?.targetMindcloudSlug);

    if (!sourceMindcloudSlug || !targetMindcloudSlug || sourceMindcloudSlug === targetMindcloudSlug) {
      continue;
    }

    const key = `${sourceMindcloudSlug}\u0000${targetMindcloudSlug}`;
    const existing = pairByKey.get(key);
    pairByKey.set(key, {
      sourceMindcloudSlug,
      targetMindcloudSlug,
      lastModified: getLatestTimestamp(existing?.lastModified, pair?.lastModified)
    });
  }

  return [...pairByKey.values()].sort((left, right) => {
    return left.sourceMindcloudSlug.localeCompare(right.sourceMindcloudSlug) || left.targetMindcloudSlug.localeCompare(right.targetMindcloudSlug);
  });
};
